import type { ProfileVisibleRow } from '../lib/db-types';

/**
 * Editar la ficha de un trabajador que ya está en el padrón.
 *
 * Pedido del usuario (9-set): *«necesito poder tener la opción de edición en la base de
 * trabajadores. Algunas han llenado mal algunos datos y queremos tener todo prolijo»*.
 *
 * La lógica vive acá y no en la pantalla porque lo que puede salir mal no es el formulario: es QUÉ
 * se escribe en la base. Tres reglas, y las tres tienen una razón concreta:
 *
 * 1. **El documento no se edita desde acá.** El trabajador entra al sistema con
 *    `<documento>@campo.treid.pe`, así que cambiar `profiles.dni` sin cambiar el correo de la
 *    cuenta deja a la persona **sin poder entrar**, con una ficha que se ve perfecta. Además la
 *    base lo prohíbe por trigger para todo el mundo que no sea el jefe de operaciones
 *    (`profiles_guard_privilegios`). Corregir un documento mal tipeado es otra operación: hay que
 *    tocar la cuenta de acceso, no la ficha.
 * 2. **El correo de la ficha NO es el usuario con el que entra.** De 72 fichas, 31 tienen un
 *    correo distinto del de su cuenta: es un dato de contacto. La pantalla lo dice, porque quien
 *    lo edita asume lo contrario.
 * 3. **Solo se manda lo que cambió.** Un `update` que reescribe las diez columnas deja un rastro
 *    que dice que se cambió todo, y hace imposible leer después qué se corrigió de verdad. Y si no
 *    cambió nada, no se escribe: guardar sin tocar nada no puede dejar rastro de una corrección
 *    que no existió.
 */

/** Los campos de la ficha que se pueden corregir. Todos texto: es lo que da un formulario. */
export interface FormFichaTrabajador {
  apellidos: string;
  nombres: string;
  fechaNacimiento: string;
  telefono: string;
  correo: string;
  departamento: string;
  provincia: string;
  distrito: string;
  direccion: string;
  zona: string;
}

/** Las columnas de `profiles` que este formulario puede escribir, y nada más. */
export type CambiosDeFicha = Partial<{
  first_names: string;
  last_names: string;
  birth_date: string | null;
  phone: string | null;
  email: string;
  department: string | null;
  province: string | null;
  district: string | null;
  address: string | null;
  zone: string | null;
}>;

/** Cómo se llama cada columna en la pantalla, para que el aviso y el rastro se lean. */
export const ETIQUETA_DE_CAMPO: Record<keyof CambiosDeFicha, string> = {
  first_names: 'Nombres',
  last_names: 'Apellidos',
  birth_date: 'Fecha de nacimiento',
  phone: 'Teléfono',
  email: 'Correo de contacto',
  department: 'Departamento',
  province: 'Provincia',
  district: 'Distrito',
  address: 'Dirección',
  zone: 'Zona',
};

export function fichaDesdePerfil(p: ProfileVisibleRow): FormFichaTrabajador {
  return {
    apellidos: p.last_names,
    nombres: p.first_names,
    fechaNacimiento: p.birth_date ?? '',
    telefono: p.phone ?? '',
    correo: p.email,
    departamento: p.department ?? '',
    provincia: p.province ?? '',
    distrito: p.district ?? '',
    direccion: p.address ?? '',
    zona: p.zone ?? '',
  };
}

/**
 * Qué falta para poder guardar, en el orden en que se lee el formulario. `null` = se puede.
 *
 * Devuelve el TEXTO y no un booleano, igual que el formulario de alta: un botón apagado sin decir
 * por qué obliga a adivinar cuál de diez campos estorba.
 *
 * El correo **es obligatorio** —la columna es `not null` y es el identificador de la cuenta de
 * campo— así que vaciarlo no es una corrección: es romper la ficha.
 */
export function loQueFaltaEnFicha(f: FormFichaTrabajador): string | null {
  if (f.apellidos.trim() === '') return 'Faltan los apellidos.';
  if (f.nombres.trim() === '') return 'Faltan los nombres.';
  if (f.correo.trim() === '') return 'El correo no puede quedar vacío.';
  if (!f.correo.includes('@')) return 'Ese correo no parece un correo.';
  if (f.telefono.trim() !== '' && !/^[\d\s+()-]{6,20}$/.test(f.telefono.trim())) {
    return 'Ese teléfono no parece un teléfono.';
  }
  // Una fecha de nacimiento en el futuro es siempre un error de tipeo, y pasa: el selector de
  // fecha arranca en el año en curso y basta equivocarse en el año.
  if (f.fechaNacimiento !== '' && f.fechaNacimiento > new Date().toISOString().slice(0, 10)) {
    return 'La fecha de nacimiento no puede ser futura.';
  }
  return null;
}

/** Un texto opcional listo para la base: vacío es `null` y no cadena vacía. */
function opcional(v: string): string | null {
  const t = v.trim();
  return t === '' ? null : t;
}

/**
 * Solo lo que cambió respecto de la ficha guardada.
 *
 * Se compara **después de recortar espacios**, así que agregar y borrar un espacio no cuenta como
 * cambio. Un objeto vacío significa que no hay nada que guardar, y la pantalla lo trata como tal:
 * no escribe, no audita y lo dice.
 *
 * Los nombres se mandan tal como se escribieron: la base los normaliza sola con el trigger
 * `nombre_propio` de la 0103, así que da igual si alguien los teclea en mayúsculas.
 */
export function cambiosDeFicha(
  original: ProfileVisibleRow,
  f: FormFichaTrabajador,
): CambiosDeFicha {
  const c: CambiosDeFicha = {};
  if (f.nombres.trim() !== original.first_names) c.first_names = f.nombres.trim();
  if (f.apellidos.trim() !== original.last_names) c.last_names = f.apellidos.trim();
  if (opcional(f.fechaNacimiento) !== original.birth_date) {
    c.birth_date = opcional(f.fechaNacimiento);
  }
  if (opcional(f.telefono) !== original.phone) c.phone = opcional(f.telefono);
  if (f.correo.trim() !== original.email) c.email = f.correo.trim();
  if (opcional(f.departamento) !== original.department) c.department = opcional(f.departamento);
  if (opcional(f.provincia) !== original.province) c.province = opcional(f.provincia);
  if (opcional(f.distrito) !== original.district) c.district = opcional(f.distrito);
  if (opcional(f.direccion) !== original.address) c.address = opcional(f.direccion);
  if (opcional(f.zona) !== original.zone) c.zone = opcional(f.zona);
  return c;
}

/** «Teléfono, Distrito y Zona»: para el aviso de «guardado» y para el rastro. */
export function nombresDeLosCambios(c: CambiosDeFicha): string[] {
  return (Object.keys(c) as (keyof CambiosDeFicha)[]).map((k) => ETIQUETA_DE_CAMPO[k]);
}
